// src/utils/ensureExertionAdvice.js
import { getFirestore, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getExertionAdvice } from "../api/generateExertionAdvice";

/**
 * Ensure exertion AI advice exists for this measurement.
 *
 * If `existingAdvice` is provided, it is returned immediately.
 * Otherwise, we call the Cloud Function and persist the result under:
 *   measurements/{uid}/breatheultra/{baseName}.aiCoachResponse.exertion
 *
 * Expected stats shape (all but core metrics optional):
 * {
 *   timestamp?: string,
 *   rrBpm: number,
 *   br1min: number,
 *   br2min: number,
 *   rt90: string,
 *   rt95: string,
 *   athleteLevel?: "beginner" | "intermediate" | "advanced",
 *   sportFocus?: string,
 *   rrHistory?: { label: string, rrBpm: number }[],
 *   activityTitle?: string,
 *   activityExercise?: string,
 *   activitySport?: string,
 *   activityDurationMin?: number,
 *   activityIntensity?: string,
 *   activityDescription?: string,
 * }
 */
export async function ensureExertionAdvice({
  uid,
  baseName,
  stats,
  existingAdvice, // data?.aiCoachResponse?.exertion?.adviceByGroup
}) {
  // 1) Already have advice from Firestore → just return it
  if (existingAdvice) {
    return existingAdvice;
  }

  // 2) No advice yet → call OpenAI via CF
  const res = await getExertionAdvice(stats);
  const adviceByGroup = res.adviceByGroup || null;

  // 3) Save to Firestore so we don't recompute next time
  const db = getFirestore();
  const ref = doc(db, "measurements", uid, "breatheultra", baseName);

  // Allow for backwards compatibility if something *still* sends nested activityDetails
  const nested = stats.activityDetails || {};
  const snapshot = {
    rrBpm: stats.rrBpm,
    br1min: stats.br1min,
    br2min: stats.br2min,
    rt90: stats.rt90,
    rt95: stats.rt95,
    athleteLevel: stats.athleteLevel ?? null,
    sportFocus: stats.sportFocus ?? null,
    rrHistory: stats.rrHistory ?? null,

    activityTitle: stats.activityTitle ?? nested.title ?? null,
    activityExercise: stats.activityExercise ?? nested.exercise ?? null,
    activitySport: stats.activitySport ?? nested.sport ?? null,
    activityDurationMin:
      stats.activityDurationMin ?? nested.duration_min ?? null,
    activityIntensity: stats.activityIntensity ?? nested.intensity ?? null,
    activityDescription:
      stats.activityDescription ?? nested.description ?? null,
  };

  await setDoc(
    ref,
    {
      aiCoachResponse: {
        exertion: {
          adviceByGroup,
          createdAt: serverTimestamp(),
          model: "gpt-5-mini",
          statsSnapshot: snapshot,
        },
      },
    },
    { merge: true }
  );

  return adviceByGroup;
}
